/**
 * Update Position Dimensions dialog.
 *
 * Stages a Role Master request to correct N positions' Financial Dimensions
 * (campus/dept/faculty) to Finance's budget values. Shows the per-position
 * delta (current -> new), lets the operator DESELECT any row before staging
 * (e.g. a position that genuinely belongs elsewhere -> Create instead), and
 * blocks conflicting same-position selections. On approval (Requests tab) the
 * dims are written to F&O and mirrored locally — nothing writes un-reviewed.
 */
import React, { useMemo, useState } from 'react';
import { Loader2, CheckCircle, AlertTriangle, Ruler } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { organizationService, FinanceWorksheetItem } from '@/services/api/organizationService';

export interface DimUpdateRow {
  item: FinanceWorksheetItem;
  position_id: number;        // local Position.id (the suggestion's)
  fo_position_id: string;
  title: string;
  old: { campus: string; department: string; faculty: string };
  next: { campus: string; department: string; faculty: string };
}

const FIELDS: Array<{ key: 'campus' | 'department' | 'faculty'; label: string }> = [
  { key: 'campus', label: 'Campus' },
  { key: 'department', label: 'Dept' },
  { key: 'faculty', label: 'Faculty' },
];

interface Props {
  rows: DimUpdateRow[];
  budgetYear?: number;
  onClose: () => void;
  onStaged: () => void;
}

const UpdateDimensionsDialog: React.FC<Props> = ({ rows, budgetYear, onClose, onStaged }) => {
  const [deselected, setDeselected] = useState<Set<string>>(new Set());
  const [justification, setJustification] = useState(`Budget ${budgetYear ?? ''} — Role Master dimension alignment`.trim());
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  const selected = useMemo(() => rows.filter(r => !deselected.has(r.fo_position_id)), [rows, deselected]);

  // Conflict: same F&O position targeted with DIFFERENT new dims across selected rows.
  const conflict = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of selected) {
      const sig = `${r.next.campus}|${r.next.department}|${r.next.faculty}`;
      const prev = seen.get(r.fo_position_id);
      if (prev && prev !== sig) return r.fo_position_id;
      seen.set(r.fo_position_id, sig);
    }
    return null;
  }, [selected]);

  const toggle = (foId: string) => setDeselected(prev => {
    const n = new Set(prev);
    if (n.has(foId)) n.delete(foId); else n.add(foId);
    return n;
  });

  const handleStage = async () => {
    if (!selected.length || conflict) return;
    setSubmitting(true);
    try {
      // Dedupe by fo_position_id (selected rows can repeat a seat with identical target dims).
      const byFo = new Map<string, DimUpdateRow>();
      for (const r of selected) if (!byFo.has(r.fo_position_id)) byFo.set(r.fo_position_id, r);
      await organizationService.createUpdateDimensionsRequest({
        items: Array.from(byFo.values()).map(r => ({
          position_id: r.position_id,
          fo_position_id: r.fo_position_id,
          new_campus: r.next.campus || null,
          new_department: r.next.department || null,
          new_faculty: r.next.faculty || null,
        })),
        business_justification: justification || undefined,
      });
      setResult({ ok: true, message: `Staged a request to correct ${byFo.size} position(s). Pending review on the Requests tab.` });
      onStaged();
    } catch (e: any) {
      setResult({ ok: false, message: e?.response?.data?.detail || e?.message || 'Failed to stage the request.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Ruler className="h-5 w-5 text-gray-400" /> Update dimensions</SheetTitle>
          <SheetDescription>Correct these positions' financial dimensions to the budget values.</SheetDescription>
        </SheetHeader>

        {result ? (
          <div className="mt-6 space-y-4">
            <div className={cn('rounded p-4 text-center', result.ok ? 'bg-emerald-50' : 'bg-red-50')}>
              {result.ok ? <CheckCircle className="h-8 w-8 text-emerald-600 mx-auto mb-2" /> : <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />}
              <p className={cn('text-sm', result.ok ? 'text-emerald-800' : 'text-red-700')}>{result.message}</p>
            </div>
            <SheetFooter><Button onClick={onClose}>Done</Button></SheetFooter>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="bg-blue-50 rounded p-3 text-xs text-blue-700 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>This <strong>stages</strong> a request — F&O dimensions are written only after a reviewer approves it on the Requests tab. Deselect any position that genuinely belongs elsewhere (use Create instead).</div>
            </div>

            <div className="text-xs text-gray-500">{selected.length} of {rows.length} selected</div>

            <div className="max-h-[52vh] overflow-y-auto rounded border divide-y">
              {rows.map((r) => {
                const on = !deselected.has(r.fo_position_id);
                return (
                  <label key={r.fo_position_id + r.position_id} className="flex items-start gap-3 px-3 py-2 text-xs cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" checked={on} onChange={() => toggle(r.fo_position_id)} className="mt-0.5 accent-heritage-blue" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{r.title} <span className="font-mono text-gray-400">· {r.fo_position_id}</span></div>
                      <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                        {FIELDS.map(f => {
                          const o = r.old[f.key], n = r.next[f.key];
                          if (o === n) return null;
                          return (
                            <span key={f.key} className="tabular-nums">
                              {f.label}: <span className="text-amber-600">{o || '—'}</span>
                              <span className="text-gray-400"> → </span>
                              <span className="text-heritage-blue font-medium">{n || '—'}</span>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            {conflict && (
              <div className="rounded bg-red-50 border border-red-100 p-2 text-xs text-red-700">
                Position {conflict} is selected with two different target dimensions — deselect one (or use Create for the odd one out) before staging.
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-700">Business justification</label>
              <Input value={justification} onChange={e => setJustification(e.target.value)} />
            </div>

            <SheetFooter className="mt-2">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={handleStage} disabled={submitting || !selected.length || !!conflict}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Stage update ({selected.length})
              </Button>
            </SheetFooter>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default UpdateDimensionsDialog;
